import React from 'react'
import { useTranslation } from 'react-i18next'
import { Vehicle, VehicleStatus } from '../types'
import { 
  MapPin, 
  User, 
  Battery, 
  Signal, 
  Clock, 
  Gauge, 
  Navigation, 
  Fuel,
  Thermometer,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

interface VehicleInfoProps {
  vehicle: Vehicle | null
}

const VehicleInfo: React.FC<VehicleInfoProps> = ({ vehicle }) => {
  const { t } = useTranslation()

  if (!vehicle) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <MapPin className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>{t('select_vehicle')}</p>
          <p className="text-sm">{t('to_see_info')}</p>
        </div>
      </div>
    )
  }

  const getStatusIcon = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.MOVING:
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case VehicleStatus.STOPPED:
        return <XCircle className="w-5 h-5 text-red-500" />
      case VehicleStatus.IDLE:
        return <Clock className="w-5 h-5 text-yellow-500" />
      case VehicleStatus.OFFLINE:
        return <XCircle className="w-5 h-5 text-gray-500" />
      case VehicleStatus.MAINTENANCE:
        return <AlertTriangle className="w-5 h-5 text-orange-500" />
      default:
        return <XCircle className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusText = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.MOVING:
        return 'En movimiento'
      case VehicleStatus.STOPPED:
        return 'Detenido'
      case VehicleStatus.IDLE:
        return 'Inactivo'
      case VehicleStatus.OFFLINE:
        return 'Sin conexión'
      case VehicleStatus.MAINTENANCE:
        return 'Mantenimiento'
      default:
        return 'Desconocido'
    }
  }

  const getBatteryColor = (level?: number) => {
    if (!level) return 'text-gray-400'
    if (level > 60) return 'text-green-500'
    if (level > 30) return 'text-yellow-500'
    return 'text-red-500'
  }

  const getSignalColor = (strength?: number) => {
    if (!strength) return 'text-gray-400'
    if (strength > 70) return 'text-green-500'
    if (strength > 40) return 'text-yellow-500'
    return 'text-red-500'
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 space-y-6">
        {/* Vehicle Header */}
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {vehicle.name}
          </h2>
          <p className="text-sm text-gray-600">{vehicle.plateNumber}</p>
        </div>

        {/* Status Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-3">{t('current_status')}</h3>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {getStatusIcon(vehicle.status)}
              <span className="text-sm font-medium">{getStatusText(vehicle.status)}</span>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Última actualización: {format(vehicle.lastUpdate, 'dd/MM/yyyy HH:mm:ss', { locale: es })}
          </div>
        </div>

        {/* Driver Information */}
        {vehicle.driver && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-800 mb-3">{t('driver')}</h3>
            <div className="flex items-center space-x-2 mb-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{vehicle.driver.name}</span>
            </div>
            {vehicle.driver.phone && (
              <div className="text-xs text-gray-600">
                Tel: {vehicle.driver.phone}
              </div>
            )}
          </div>
        )}

        {/* Location Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-3">{t('location')}</h3>
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="text-sm">
                {vehicle.location.address || t('address_not_available')}
                <div className="text-xs text-gray-500 mt-1">
                  {vehicle.location.latitude.toFixed(6)}, {vehicle.location.longitude.toFixed(6)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Metrics */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-3">{t('metrics')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <Gauge className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <div className="text-lg font-semibold">{vehicle.speed}</div>
              <div className="text-xs text-gray-600">km/h</div>
            </div>
            <div className="text-center">
              <Navigation className="w-6 h-6 text-purple-500 mx-auto mb-1" />
              <div className="text-lg font-semibold">{vehicle.heading}°</div>
              <div className="text-xs text-gray-600">{t('heading')}</div>
            </div>
          </div>
        </div>

        {/* Device Status */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-3">{t('device_status')}</h3>
          <div className="space-y-3">
            {vehicle.batteryLevel && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Battery className={`w-4 h-4 ${getBatteryColor(vehicle.batteryLevel)}`} />
                  <span className="text-sm">{t('battery')}</span>
                </div>
                <span className="text-sm font-medium">{vehicle.batteryLevel}%</span>
              </div>
            )}
            
            {vehicle.signalStrength && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Signal className={`w-4 h-4 ${getSignalColor(vehicle.signalStrength)}`} />
                  <span className="text-sm">{t('signal')}</span>
                </div>
                <span className="text-sm font-medium">{vehicle.signalStrength}%</span>
              </div>
            )}

            {vehicle.fuel && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Fuel className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">{t('fuel')}</span>
                </div>
                <span className="text-sm font-medium">{vehicle.fuel}%</span>
              </div>
            )}

            {vehicle.temperature && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Thermometer className="w-4 h-4 text-red-500" />
                  <span className="text-sm">{t('temperature')}</span>
                </div>
                <span className="text-sm font-medium">{vehicle.temperature}°C</span>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        {(vehicle.odometer || vehicle.engineHours) && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-800 mb-3">{t('additional_info')}</h3>
            <div className="space-y-2">
              {vehicle.odometer && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('odometer')}:</span>
                  <span className="font-medium">{vehicle.odometer.toLocaleString()} km</span>
                </div>
              )}
              {vehicle.engineHours && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('engine_hours')}:</span>
                  <span className="font-medium">{vehicle.engineHours.toLocaleString()} h</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <button className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors">
            {t('view_full_history')}
          </button>
          <button className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors">
            {t('generate_report')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VehicleInfo
